import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { OverlayDismissDirective } from '../../directives/overlay-dismiss.directive';
import { PixelCreatureComponent } from '../pixel-creature/pixel-creature.component';
import { MonStaticSpriteComponent } from '../mon-static-sprite/mon-static-sprite.component';
import { AddressColorPipe } from '../../pipes/address-color.pipe';
import { ChallengeService } from '../../services/challenge.service';
import { ServerService } from '../../services/server.service';
import { TeamListService } from '../../services/team-list.service';
import { formatPlayerName } from '../../services/utils.service';

// The invitee's view of a party (a PARTY_CARD): the matchup + your seat, Sign & Claim Seat → the
// per-seat consent. Mounted in home; shows itself whenever an invite is pending.
@Component({
  selector: 'app-party-invite-card',
  imports: [NgTemplateOutlet, OverlayDismissDirective, PixelCreatureComponent, MonStaticSpriteComponent, AddressColorPipe],
  templateUrl: './party-invite-card.component.html',
  styleUrls: ['./party-invite-card.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartyInviteCardComponent {
  protected challenge = inject(ChallengeService);
  private teamList = inject(TeamListService);
  private server = inject(ServerService);

  readonly card = this.challenge.incomingPartyCard;
  readonly result = this.challenge.seatClaimResult;
  readonly activeTeam = computed(() => this.teamList.teams().find((t) => t.isActive) ?? null);

  // Canonical seats [p0, p2, p1, p3]: idx 0/1 = side 0 (host's side, "A"), 2/3 = side 1 ("B").
  readonly mySide = computed(() => {
    const idx = this.card()?.yourSeatIndex;
    return idx == null ? null : idx < 2 ? 0 : 1;
  });

  // 2×2 matchup cells in render order: top row = opponents' side, bottom row = your side. Each cell
  // keeps its canonical seat index (for the your-seat highlight) and its side letter (edge color).
  readonly matchCells = computed(() => {
    const c = this.card();
    const my = this.mySide();
    if (!c || my == null) return [];
    // Seats carry only a username; the X handle comes from presence.
    const cell = (idx: number) => {
      const seat = c.seats[idx];
      const name = seat.address
        ? formatPlayerName(this.server.resolveIdentity({ address: seat.address, username: seat.username }))
        : '';
      return { seat, idx, side: (idx < 2 ? 'a' : 'b') as 'a' | 'b', name };
    };
    const yourIdx = my === 0 ? [0, 1] : [2, 3];
    const oppIdx = my === 0 ? [2, 3] : [0, 1];
    return [cell(oppIdx[0]), cell(oppIdx[1]), cell(yourIdx[0]), cell(yourIdx[1])];
  });

  readonly seatCountLabel = computed(() => {
    const c = this.card();
    const n = c ? c.seats.filter((s) => !s.filled && s.kind !== 'cpu' && s.kind !== 'host').length : 0;
    return `${n} SEAT${n === 1 ? '' : 'S'}`;
  });

  claim(): void {
    const c = this.card();
    if (c) void this.challenge.claimSeat(c);
  }
  dismiss(): void {
    this.challenge.dismissInvite();
  }
  toNum(id: bigint): number {
    return Number(id);
  }
}
